import React, { useEffect, useMemo, useRef, useState } from 'react';
import './HeroChromatogram.css';

// Compound-named illustrations. Traces and results are fictional, not compound/lot data.
const EXAMPLES = [
  { name: 'BPC-157', seed: 1, purity: '99.24%', quantity: '10.0 mg' },
  { name: 'MOTS-c', seed: 2, purity: '98.86%', quantity: '5.0 mg' },
  { name: 'NAD', seed: 3, purity: '99.41%', quantity: '20.0 mg' },
];
export const DEMO_CHECKS = [
  { name: 'Purity', group: 'Purity', value: 'purity' },
  { name: 'Quantity', group: 'Quantity', value: 'quantity' },
  { name: 'Identity', group: 'Identity', result: 'Example match' },
  { name: 'Endotoxin', group: 'Endotoxin', result: 'Below demo limit' },
  { name: 'Lead', group: 'Heavy metals', result: 'Below demo limit' },
  { name: 'Arsenic', group: 'Heavy metals', result: 'Below demo limit' },
  { name: 'Cadmium', group: 'Heavy metals', result: 'Below demo limit' },
  { name: 'Mercury', group: 'Heavy metals', result: 'Below demo limit' },
  { name: 'Aerobic screen', group: 'Sterility', result: 'No growth (demo)' },
  { name: 'Fungal screen', group: 'Sterility', result: 'No growth (demo)' },
];
const GROUPS = [...new Set(DEMO_CHECKS.map(check => check.group))];
export const TRACE_MS = 1200;
export const CHECK_START_MS = 1500;
export const CHECK_MS = 200;
export const DEMO_DURATION_MS = CHECK_START_MS + DEMO_CHECKS.length * CHECK_MS;

function prefersReducedMotion() {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Graphical peaks only. Example purity/quantity are not calculated from them.
export function buildDemoTrace(seed = 1) {
  const peak = 290 + seed * 36;
  const peaks = [[102, 5, 8], [190, 3, 6], [peak, 166, 12], [peak + 100, 7, 9], [586, 4, 7]];
  const points = Array.from({ length: 321 }, (_, i) => {
    const x = 48 + i * 2;
    const signal = peaks.reduce((sum, [center, height, spread]) => sum + height * Math.exp(-((x - center) ** 2) / (2 * spread ** 2)), 0);
    return [x, 206 - signal - (Math.sin(i * 2.3 + seed) + 1) * .65];
  });
  return {
    d: points.map(([x, y], i) => (i ? 'L' : 'M') + x.toFixed(1) + ' ' + y.toFixed(1)).join(' '),
    peak,
  };
}

function DemoRun({ example }) {
  const sectionRef = useRef(null);
  const [reduced, setReduced] = useState(prefersReducedMotion);
  const [visible, setVisible] = useState(false);
  const [paused, setPaused] = useState(false);
  const [elapsed, setElapsed] = useState(() => prefersReducedMotion() ? DEMO_DURATION_MS : 0);
  const completed = Math.min(DEMO_CHECKS.length, Math.max(0, Math.floor((elapsed - CHECK_START_MS) / CHECK_MS)));
  const complete = completed === DEMO_CHECKS.length;
  const running = visible && !paused && !complete && !reduced;
  const { d, peak } = useMemo(() => buildDemoTrace(example.seed), [example.seed]);
  const reference = useMemo(() => buildDemoTrace(example.seed + .08).d, [example.seed]);

  useEffect(() => {
    if (typeof window.IntersectionObserver !== 'function') {
      setVisible(true);
      return undefined;
    }
    const observer = new window.IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.15 });
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const change = event => {
      setReduced(event.matches);
      if (event.matches) setElapsed(DEMO_DURATION_MS);
    };
    query?.addEventListener?.('change', change);
    return () => query?.removeEventListener?.('change', change);
  }, []);

  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => setElapsed(value => Math.min(DEMO_DURATION_MS, value + 100)), 100);
    return () => window.clearInterval(timer);
  }, [running]);

  const active = elapsed < CHECK_START_MS ? 'Drawing the simulated trace' : complete ? 'Demo approved' : 'Simulating ' + DEMO_CHECKS[completed].name.toLowerCase();

  return (
    <div ref={sectionRef} className="hero-chromatogram__run" data-complete={complete} data-running={running} data-reduced-motion={reduced} style={{ '--trace-duration': TRACE_MS + 'ms' }}>
      <div className="hero-chromatogram__analysis">
      <div className="hero-chromatogram__intro">
        <div>
          <h2 className="hero-chromatogram__title">A sample. Ten checkpoints.</h2>
          <p className="hero-chromatogram__description">{example.name} / illustrative scan / not actual test data</p>
        </div>
        <div className="hero-chromatogram__counter" aria-label={completed + ' of 10 simulated checks passed'}>
          <strong><b key={completed}>{String(completed).padStart(2, '0')}</b><span>/10</span></strong><span>demo passes</span>
        </div>
      </div>
      <div className="hero-chromatogram__plot" data-revealed={elapsed >= TRACE_MS}>
        <div className="hero-chromatogram__sweep" aria-hidden="true" />
        <svg className="hero-chromatogram__trace" viewBox="0 0 720 240" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Full simulated chromatogram with fictional peaks, not analytical data">
          <g className="hero-chromatogram__grid">
            {[40, 95, 150, 206].map(y => <line key={'y' + y} x1="48" y1={y} x2="688" y2={y} />)}
            {[48, 208, 368, 528, 688].map(x => <line key={'x' + x} x1={x} y1="24" x2={x} y2="206" />)}
          </g>
          <g className="hero-chromatogram__ticks" aria-hidden="true">
            <text x="32" y="210" textAnchor="end">0</text><text x="32" y="126" textAnchor="end">50</text><text x="32" y="44" textAnchor="end">100</text>
            {[0, 5, 10, 15, 20].map((value, i) => <text key={value} x={48 + i * 160} y="229" textAnchor="middle">{value}</text>)}
          </g>
          <path className="hero-chromatogram__area" d={d + ' L688 206 L48 206 Z'} data-revealed={elapsed >= TRACE_MS} />
          <path className="hero-chromatogram__reference" d={reference} />
          <path className="hero-chromatogram__line" d={d} pathLength="1" />
          <g className="hero-chromatogram__peak-label" data-revealed={elapsed >= TRACE_MS}>
            <line x1={peak + 6} y1="44" x2={peak + 36} y2="20" /><circle cx={peak} cy="40" r="4" /><text x={peak + 42} y="23">Demo peak</text>
          </g>
          <text className="hero-chromatogram__watermark" x="680" y="187" textAnchor="end">SIMULATED DATA</text>
        </svg>
      </div>
      <div className="hero-chromatogram__axis"><span>Relative signal / demo units</span><span>Illustrative time (min)</span></div>
      <div className="hero-chromatogram__legend" aria-hidden="true"><span><i />Simulated sample</span><span><i />Simulated comparison</span></div>
      <div className="hero-chromatogram__progress" role="progressbar" aria-label="Simulated test sequence" aria-valuemin={0} aria-valuemax={10} aria-valuenow={completed}>
        {DEMO_CHECKS.map((check, index) => <span key={check.name} data-passed={index < completed} data-active={!complete && elapsed >= CHECK_START_MS && index === completed} />)}
      </div>
      <div className="hero-chromatogram__status">
        <p role="status" aria-atomic="true">{paused && !complete ? 'Simulation paused' : active}<span>{complete ? 'Not a batch release.' : 'Demo only, not live testing.'}</span></p>
        <button type="button" onClick={() => setPaused(value => !value)} disabled={complete || reduced} aria-label={paused ? 'Resume testing simulation' : 'Pause testing simulation'}>{complete ? 'Complete' : paused ? 'Resume' : 'Pause'}</button>
      </div>
      </div>
      <div className="hero-chromatogram__panels">
      <div className="hero-chromatogram__mapping"><span>Simulated result map</span><span>6 categories · 10 checks</span></div>
      <div className="hero-chromatogram__results">
        {GROUPS.map((group, groupIndex) => {
          const checks = DEMO_CHECKS.map((check, index) => ({ ...check, index })).filter(check => check.group === group);
          const passed = checks.every(check => check.index < completed);
          return (
            <section key={group} className="hero-chromatogram__result" aria-label={group + ', simulated results'} data-passed={passed} data-active={!complete && elapsed >= CHECK_START_MS && DEMO_CHECKS[completed].group === group}>
              <div className="hero-chromatogram__result-heading"><h3><span aria-hidden="true">{String(groupIndex + 1).padStart(2, '0')}</span>{group}</h3><span>{passed ? '✓ Demo pass' : 'Demo pending'}</span></div>
              <ul>{checks.map(check => (
                <li key={check.name} data-passed={check.index < completed}>
                  <span>{checks.length > 1 ? check.name : 'Example result'}</span>
                  <strong>{check.index < completed ? (check.value ? example[check.value] : check.result) : 'Pending'}</strong>
                  <span className="sr-only">{check.index < completed ? 'Demo pass' : 'Demo pending'}</span>
                </li>
              ))}</ul>
            </section>
          );
        })}
      </div>
      <p className="hero-chromatogram__method-note">Separate test panels are illustrated. Endotoxin, heavy metals, and sterility are not determined by this chromatogram. Timing is compressed; values and pass criteria are invented for the demo.</p>
      </div>
    </div>
  );
}

export default function HeroChromatogram() {
  const [selected, setSelected] = useState(0);
  const [replay, setReplay] = useState(0);
  return (
    <section className="hero-chromatogram" aria-label="Simulated ten-check testing demonstration" data-testid="hero-chromatogram">
      <div className="hero-chromatogram__header">
        <span className="hero-chromatogram__label">The analytical view</span>
        <span className="hero-chromatogram__disclosure">Simulation only · Not a COA</span>
      </div>
      <p className="hero-chromatogram__warning">All results below are fictional, including every pass. Compound names label illustrations only. They do not describe any Batchtrue product or batch.</p>
      <DemoRun key={selected + '-' + replay} example={EXAMPLES[selected]} />
      <div className="hero-chromatogram__controls" role="group" aria-label="Choose a compound illustration">
        {EXAMPLES.map((item, index) => <button key={item.name} type="button" aria-pressed={index === selected} onClick={() => setSelected(index)}>{item.name}</button>)}
        <button type="button" className="hero-chromatogram__replay" onClick={() => setReplay(value => value + 1)} aria-label="Replay testing simulation">Replay ↗</button>
      </div>
      <p className="hero-chromatogram__note">Actual documentation is separate. Testing is not available for every product. Request the current product-specific COA and confirm which tests it covers before ordering.</p>
    </section>
  );
}
